import React, {useEffect, useMemo, useState} from 'react';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {Employee, NewJoiner, NewJoinerForm, NewJoinerType} from '../../api/Model';
import {inputChange, inputChangeEvent, mapSetState, patch, selectChange, SetState} from '../../util/FormHandlers';
import {EmployeeSelect} from '../../components/employee-select/EmployeeSelect';
import {DateField} from '../../components/date-field/DateField';
import {emptyEmployee} from "../../api/Empty";
import {NeighbourhoodlessEmployees} from "./NeighbourhoodlessEmployees";
import HibobIcon from "../../components/icons/HibobIcon";
import {toEmployeeRef} from "../../api/Projections";

const NEW_JOINER_TYPE_LABELS: Record<NewJoinerType, string> = {
    [NewJoinerType.InternationalRelocation]: 'International Relocation',
    [NewJoinerType.LocalRelocation]: 'Local Relocation',
    [NewJoinerType.NewFTE]: 'New Full Time Employee (FTE)',
    [NewJoinerType.NewIntern]: 'New Intern',
    [NewJoinerType.InternToFTE]: 'Intern → FTE',
    [NewJoinerType.ReturnFromParentalLeave]: 'Return from Parental Leave',
};

export function NewJoinerDialog({form, setForm}: {
    form: NewJoinerForm;
    setForm: SetState<NewJoinerForm>;
}) {
    const personType = form.person.type;
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | undefined>({...emptyEmployee, ...(form.person.type === 'existing' ? form.person.employee : undefined)});
    const [selectedManager, setSelectedManager] = useState<Employee | undefined>({...emptyEmployee, ...(form.person.type === 'new' ? form.person.manager : undefined)});

    const handlers = useMemo(() => {
        const setPerson = mapSetState(setForm, 'person');

        return ({
            setPerson,
            handlePersonTypeChange: (_: React.MouseEvent, newType: 'existing' | 'new' | null) => {
                if (!newType) return;
                setForm(prev => ({
                    ...prev,
                    person: newType === 'existing'
                        ? {type: 'existing', employee: {id: 0, fullName: '', deactivated: false}}
                        : {type: 'new', fullName: '', manager: null},
                }));
            },
            handleFullNameChange: inputChangeEvent(newValue => {
                setPerson(prevPerson => ({
                    ...prevPerson,
                    fullName: newValue,
                }));
            }),
            setJoinDate: mapSetState(setForm, 'joinDate'),
            handleTypeChange: selectChange(setForm, newValue => ({type: newValue as NewJoinerType})),
            handleRemoteChange: selectChange(setForm, newValue => ({remote: newValue === 'remote'})),
            handleNotesChange: inputChange(setForm, newValue => ({notes: newValue || null})),
        });
    }, [setForm]);

    useEffect(() => {
        patch(handlers.setPerson, prevPerson => {
            if (prevPerson.type === 'existing' && selectedEmployee) {
                return {employee: toEmployeeRef(selectedEmployee)};
            } else {
                return undefined;
            }
        });
    }, [selectedEmployee, handlers.setPerson]);

    useEffect(() => {
        patch(handlers.setPerson, prevPerson => {
            if (prevPerson.type === 'new') {
                return {manager: selectedManager && toEmployeeRef(selectedManager)};
            } else {
                return undefined;
            }
        });
    }, [selectedManager, handlers.setPerson]);

    return (
        <Stack spacing={2} direction="column" sx={{pt: 2}}>
            <ToggleButtonGroup
                value={personType}
                exclusive
                onChange={handlers.handlePersonTypeChange}
                size="small"
                fullWidth
            >
                <ToggleButton value="existing">
                    <Stack component="span" direction="row" alignItems="center" gap={1}>
                        <HibobIcon colored sx={{fontSize: 16}}/>
                        Employee from Hibob
                    </Stack>
                </ToggleButton>
                <ToggleButton value="new">Not yet in Hibob</ToggleButton>
            </ToggleButtonGroup>

            {form.person.type === 'existing' ? (
                <>
                    <EmployeeSelect
                        label="Employee"
                        required
                        fullWidth
                        employee={selectedEmployee}
                        setEmployee={setSelectedEmployee}
                    />
                    <NeighbourhoodlessEmployees
                        setSelectedEmployee={setSelectedEmployee}
                        setJoinDate={handlers.setJoinDate}
                    />
                </>
            ) : (
                <>
                    <TextField
                        required
                        autoFocus
                        label="Full Name"
                        fullWidth
                        value={form.person.type === 'new' ? form.person.fullName : ''}
                        onChange={handlers.handleFullNameChange}
                    />
                    <EmployeeSelect
                        required
                        label="Manager"
                        fullWidth
                        employee={selectedManager}
                        setEmployee={setSelectedManager}
                    />
                </>
            )}

            <DateField
                label="Join Date"
                value={form.joinDate}
                setValue={handlers.setJoinDate}
                slotProps={{textField: {required: true, fullWidth: true}}}
            />

            <FormControl fullWidth required>
                <InputLabel id="new-joiner-type-label">Type</InputLabel>
                <Select
                    labelId="new-joiner-type-label"
                    value={form.type}
                    onChange={handlers.handleTypeChange}
                    label="Type"
                >
                    {Object.values(NewJoinerType).map(t => (
                        <MenuItem key={t} value={t}>{NEW_JOINER_TYPE_LABELS[t]}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth required>
                <InputLabel id="new-joiner-remote-label">Work Location</InputLabel>
                <Select
                    labelId="new-joiner-remote-label"
                    value={form.remote ? 'remote' : 'office'}
                    onChange={handlers.handleRemoteChange}
                    label="Work Location"
                >
                    <MenuItem value="office">Works from office</MenuItem>
                    <MenuItem value="remote">Remote</MenuItem>
                </Select>
            </FormControl>

            <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={form.notes ?? ''}
                onChange={handlers.handleNotesChange}
            />
        </Stack>
    );
}

export function newJoinerToForm(newJoiner: NewJoiner): NewJoinerForm {
    return {
        person: newJoiner.person,
        joinDate: newJoiner.joinDate,
        type: newJoiner.type,
        remote: newJoiner.remote,
        notes: newJoiner.notes ?? null,
        checklist: newJoiner.checklist,
    };
}
